import * as React from 'react'
import { useLocation } from 'react-router-dom'

import { useAiChat } from '@factorialco/f0-react'
import { useCoAgent, useCopilotChat } from '@copilotkit/react-core'

import { useOneCreateActionState } from 'modules/core/lib/oneCreateActions'

import { AiAgentGlobalActions } from 'modules/ai/components/AiAgentGlobalActions'

export type OneSharedState<T = unknown> = {
  platform: 'web' | 'mobile'
  requiredSkills: string[]
  coCreationData: T | null
  files?: OneFile[] | null
  currentPage: {
    url: string
    title: string
  }
}

export type OneFile = {
  type: 'binary'
  url?: string
  filename?: string
  mimeType?: string
}

type OneContextValue = {
  state: OneSharedState
  setState: (data: OneSharedState) => void
  isThinking: boolean
}

const OneContext = React.createContext<OneContextValue | null>(null)
const OneAvailableContext = React.createContext<boolean>(false)

export const useOneFeatureAvailable = () => React.useContext(OneAvailableContext)

const DisabledOneProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()

  // Build full URL from React Router's location so we stay in sync with the router (avoids
  // window.location timing issues). Origin is from window so we still get a full href.
  const currentPageFromLocation = React.useMemo(
    () => ({
      url: `${window.location.origin}${location.pathname}${location.search}${location.hash ?? ''}`,
      title: document.title,
    }),
    [location.pathname, location.search, location.hash]
  )

  const [state, setState] = React.useState<OneSharedState>(() => ({
    platform: 'web' as const,
    requiredSkills: [],
    coCreationData: null,
    files: null,
    currentPage: currentPageFromLocation,
  }))

  // Keep state.currentPage in sync with the router. When the URL changes, also reset
  // coCreationData and requiredSkills so consumers (e.g. useOne) re-run their init effect
  // and set fresh state for the new page (e.g. a different form's questionnaire).
  React.useEffect(() => {
    setState((prev) => {
      const urlOrTitleChanged =
        prev.currentPage.url !== currentPageFromLocation.url ||
        prev.currentPage.title !== currentPageFromLocation.title
      if (!urlOrTitleChanged) return prev
      return {
        ...prev,
        currentPage: currentPageFromLocation,
        coCreationData: null,
        files: null,
        requiredSkills: [],
      }
    })
  }, [currentPageFromLocation, setState])

  const value = React.useMemo<OneContextValue>(() => {
    return { state, setState, isThinking: false }
  }, [state, setState])

  return <OneContext.Provider value={value}>{children}</OneContext.Provider>
}

const InternalOneProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const { setInitialMessage, setFooter, setPlaceholders, setVisualizationMode } = useAiChat()
  const initialState = React.useMemo<OneSharedState>(() => {
    return {
      platform: 'web',
      requiredSkills: [],
      coCreationData: null,
      files: null,
      currentPage: {
        url: window.location.href,
        title: document.title,
      },
    }
  }, [])

  const { state: rawState, setState } = useCoAgent<OneSharedState>({
    name: 'one-workflow',
    initialState,
  })

  // CopilotKit 1.54.0 may return an empty or partial state object before the
  // agent connection is established. Merge with initialState to guarantee
  // all required fields (like currentPage) are always present. Memoize to
  // avoid creating a new object on every render (which would re-trigger
  // effects that depend on state and cause unnecessary context re-renders).
  const state: OneSharedState = React.useMemo(
    () => ({
      platform: rawState?.platform ?? initialState.platform,
      requiredSkills: rawState?.requiredSkills ?? initialState.requiredSkills,
      coCreationData: rawState?.coCreationData ?? initialState.coCreationData,
      files: rawState?.files ?? initialState.files,
      currentPage: {
        url: rawState?.currentPage?.url ?? initialState.currentPage.url,
        title: rawState?.currentPage?.title ?? initialState.currentPage.title,
      },
    }),
    [rawState, initialState]
  )

  const { isLoading: isThinking } = useCopilotChat()

  // All external setters (from useAiChat and useCoAgent) are unstable references that
  // change on every render. Keep refs so the URL-sync effect doesn't re-fire endlessly.
  const setInitialMessageRef = React.useRef(setInitialMessage)
  setInitialMessageRef.current = setInitialMessage
  const setFooterRef = React.useRef(setFooter)
  setFooterRef.current = setFooter
  const setPlaceholdersRef = React.useRef(setPlaceholders)
  setPlaceholdersRef.current = setPlaceholders
  const setVisualizationModeRef = React.useRef(setVisualizationMode)
  setVisualizationModeRef.current = setVisualizationMode
  const setStateRef = React.useRef(setState)
  setStateRef.current = setState
  const stateRef = React.useRef(state)
  stateRef.current = state

  // Always keep current page in sync. Do not apply any change when the URL is an activity (hash).
  // When the URL changes, reset co-creation state, restore the default initial message, clear the footer and placeholders, and return to sidepanel.
  React.useEffect(() => {
    const nextPage = { url: window.location.href, title: document.title }
    if (window.location.hash) return
    if (
      stateRef.current.currentPage.url === nextPage.url &&
      stateRef.current.currentPage.title === nextPage.title
    ) {
      return
    }
    if (stateRef.current.currentPage.url !== nextPage.url) {
      setInitialMessageRef.current(undefined)
      setFooterRef.current(undefined)
      setPlaceholdersRef.current([])
      setVisualizationModeRef.current('sidepanel')
      setStateRef.current({
        ...stateRef.current,
        currentPage: nextPage,
        coCreationData: null,
        files: null,
        requiredSkills: [],
      })
    } else {
      setStateRef.current({ ...stateRef.current, currentPage: nextPage })
    }
  }, [location.key])

  const value = React.useMemo<OneContextValue>(() => {
    return { state, setState, isThinking }
  }, [state, setState, isThinking])

  return <OneContext.Provider value={value}>{children}</OneContext.Provider>
}

type UseOneResult<T> = {
  coCreationData: T
  setCoCreationData: (data: T) => void
  isThinking: boolean
}

export type UseOneOptions = {
  /** When true, opens the AI chat panel when the component mounts. Only has effect when used inside the layout that provides the chat (e.g. FactorialOneLayout). */
  autoOpenChat?: boolean
  /** When set, this message is shown as the initial user message in the chat when the panel opens (e.g. when using autoOpenChat). */
  initialMessage?: string
  /** When true, opens the chat in fullscreen mode (uses setVisualizationMode). Only has effect when autoOpenChat is true. */
  fullscreen?: boolean
  /** Optional footer content (e.g. action buttons) rendered below the chat textarea. Cleared on URL change. */
  footer?: React.ReactNode
  /** Optional placeholder strings shown as suggestions in the chat input. Cleared on URL change. */
  placeholders?: string[]
  /** Optional files passed to One alongside co-creation data. */
  files?: OneFile[] | null
  /** Stable key so the effect re-runs when footer intent changes (e.g. pass `canCreateSurvey`). Avoids listing `footer` in deps and prevents update loops. */
  footerKey?: string | number | boolean
  /** Stable key so the effect re-runs when placeholders intent changes (e.g. pass `placeholders.length`). */
  placeholdersKey?: string | number
}

export const useOne = <T,>(
  requiredSkills: string[],
  coCreationData: T,
  options?: UseOneOptions
): UseOneResult<T> => {
  const ctx = React.useContext(OneContext)
  if (!ctx) {
    throw new Error('useOne must be used within OneProvider')
  }

  // Keep a ref to ctx so effects can read/write the latest state without
  // having ctx in their dependency arrays. ctx changes on every state update
  // (new object from useMemo in the provider), which would re-trigger effects
  // that call ctx.setState and create infinite loops.
  const ctxRef = React.useRef(ctx)
  ctxRef.current = ctx

  const { setOpen: setChatOpen, setInitialMessage, setVisualizationMode, setFooter } = useAiChat()
  const { data: dataFromRedirect } = useOneCreateActionState<T>()

  // All useAiChat setters (setChatOpen, setInitialMessage, setVisualizationMode, setFooter)
  // are NOT stable references — they change on every render. Capture them in refs so they
  // can be called from effects without being listed as dependencies.
  const setChatOpenRef = React.useRef(setChatOpen)
  setChatOpenRef.current = setChatOpen
  const setInitialMessageRef = React.useRef(setInitialMessage)
  setInitialMessageRef.current = setInitialMessage
  const setVisualizationModeRef = React.useRef(setVisualizationMode)
  setVisualizationModeRef.current = setVisualizationMode
  const setFooterRef = React.useRef(setFooter)
  setFooterRef.current = setFooter

  // requiredSkills is typically a new array literal on every render (e.g. ['surveys']).
  // Stabilize it so it only triggers effects when the actual contents change.
  const requiredSkillsKey = JSON.stringify(requiredSkills)
  const stableRequiredSkills = React.useMemo(() => requiredSkills, [requiredSkillsKey])

  React.useEffect(() => {
    if (options?.autoOpenChat) {
      setVisualizationModeRef.current(options.fullscreen ? 'fullscreen' : 'sidepanel')
      if (options.initialMessage !== undefined) {
        setInitialMessageRef.current(options.initialMessage)
      }
      setChatOpenRef.current(true)
    }
  }, [options?.autoOpenChat, options?.fullscreen, options?.initialMessage])

  // footerKey provides a stable primitive dep to avoid loops when footer JSX gets new
  // refs each render. setFooter is accessed via ref to avoid unstable dep.
  const footerDep = options?.footerKey !== undefined ? options.footerKey : options?.footer
  React.useEffect(() => {
    setFooterRef.current(options?.footer ?? undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footerDep])

  // Tracks whether the init effect has already run, to prevent infinite re-firing
  // when coCreationData is intentionally null (e.g. useOne<null>(['surveys'], null, ...)).
  const hasInitialized = React.useRef(false)

  React.useEffect(() => {
    if (hasInitialized.current) return
    if (ctxRef.current.state.coCreationData !== null) {
      hasInitialized.current = true
      return
    }

    const newCoCreationData = dataFromRedirect ?? coCreationData
    const initialState: OneSharedState<T> = {
      platform: 'web' as const,
      requiredSkills: stableRequiredSkills,
      coCreationData: newCoCreationData,
      files: options?.files ?? null,
      currentPage: { url: window.location.href, title: document.title },
    }

    hasInitialized.current = true
    ctxRef.current.setState(initialState)
  }, [coCreationData, stableRequiredSkills, dataFromRedirect, options?.files])

  React.useEffect(() => {
    const nextFiles = options?.files ?? null

    if (ctxRef.current.state.files === nextFiles) return

    ctxRef.current.setState({ ...ctxRef.current.state, files: nextFiles })
  }, [options?.files])

  const setCoCreationData = React.useCallback((data: T) => {
    ctxRef.current.setState({ ...ctxRef.current.state, coCreationData: data })
  }, [])

  return {
    coCreationData: ctx.state.coCreationData as T,
    setCoCreationData: setCoCreationData,
    isThinking: ctx.isThinking,
  }
}

export const OneProvider = ({
  children,
  enabled,
  available = false,
}: {
  children: React.ReactNode
  enabled: boolean
  available?: boolean
}) => {
  const inner = enabled ? (
    <InternalOneProvider>
      <AiAgentGlobalActions />
      {children}
    </InternalOneProvider>
  ) : (
    <DisabledOneProvider>{children}</DisabledOneProvider>
  )

  return <OneAvailableContext.Provider value={available}>{inner}</OneAvailableContext.Provider>
}
